<template>
	<AdminContainer
		selected="manage-dev-tickets"
		:loading="dataStatus == 'pending'"
		:fetched="dataStatus == 'success'"
		:error="error"
		@try-again="refresh"
	>
		<div class="mx-6 my-6 flex flex-col lg:mx-8">
			<div class="mb-6 flex items-center">
				<Icon
					v-if="state.isSinglePage"
					name="mdi:arrow-left"
					class="text-neutral-800 me-3 h-8 w-8 cursor-pointer duration-300 hover:text-secondary rtl:rotate-180"
					@click="
						() => {
							state.targetTicket = null
							state.targetMessage = null
							state.isSinglePage = false
						}
					"
				/>
				<span class="text-neutral-800 me-4 text-xl font-bold">
					{{ state.isSinglePage ? $t("technical_ticket") : $t("technical_tickets") }}
				</span>
				<span
					class="ripple-bg-green-500 text-white cursor-pointer rounded-md px-3 py-2 text-sm font-bold"
					@click="state.isSinglePage ? openCreateMessageDialog() : openCreateDialog()"
				>
					{{ state.isSinglePage ? $t("new_message") : $t("create") }}
				</span>
				<div class="flex-grow" />
				<form v-if="!state.isSinglePage" class="relative" @submit.prevent="search">
					<input
						v-model="state.searchText"
						class="bg-white text-neutral-800 border-gray-300 w-56 appearance-none rounded-full border-2 py-2 pe-12 ps-4 text-sm outline-none duration-300 focus:border-secondary"
						:placeholder="$t('search')"
					/>
					<Icon
						name="mdi:magnify"
						class="text-neutral-500 absolute bottom-0 end-0 top-0 my-auto me-3 h-6 w-6 cursor-pointer duration-300 hover:text-secondary"
						@click="search"
					/>
				</form>
			</div>
			<template v-if="!state.isSinglePage">
				<div class="mt-4 flex items-center">
					<div class="bg-white border-gray-200 flex items-center self-start rounded-md border px-3 py-1">
						<span class="text-neutral-800 border-gray-200 me-3 border-e pe-3 text-xs">
							{{ $t("status") }}
						</span>
						<div class="flex flex-wrap items-center gap-2">
							<span
								v-for="(s, i) in statuses"
								:key="i"
								class="cursor-pointer whitespace-nowrap rounded-md border px-2 py-1 text-xs duration-300"
								:class="{
									'border-opacity-100 bg-opacity-10': s.value === status.value,
									[status.borderClass]: s.value === status.value,
									[status.bgClass]: s.value === status.value,
									[status.txtClass]: s.value === status.value,
									'text-neutral-700 border-opacity-0 bg-opacity-0 ': s.value !== status.value
								}"
								@click="changeStatus(s.value)"
							>
								{{ s.name }}
							</span>
						</div>
					</div>
					<div class="flex-grow" />
				</div>
				<div class="my-8 flex flex-col gap-8">
					<AdminDevTicketCard
						v-for="ticket in data?.tickets"
						:key="ticket.id"
						:data="ticket"
						@status="openChangeStatusDialog(ticket)"
						@delete="openDeleteDialog(ticket)"
						@more="openTicket(ticket)"
					/>
					<div
						v-if="!data?.tickets?.length"
						class="text-gray-300 select-none py-24 text-center text-4xl font-black"
					>
						{{ $t("nothing_found") }}
					</div>
				</div>
				<BasePagination
					:total="data?.total || 0"
					:limit="state.limit"
					:current="page"
					:link="$routeUrl.ManageDevTicketsPageUrl"
					class="mb-12 mt-4 self-center"
				/>
			</template>
			<template v-else>
				<AdminDevTicketCard
					:data="state.targetTicket"
					hide-more
					@status="state.targetTicket && openChangeStatusDialog(state.targetTicket)"
					@delete="state.targetTicket && openDeleteDialog(state.targetTicket)"
				/>
				<div class="my-8 flex flex-col gap-8">
					<AdminDevTicketMessageCard
						v-for="message in state.targetTicket?.messages
							?.slice()
							?.sort((a, b) => b.created.localeCompare(a.created))"
						:key="message.id"
						:data="message"
						@delete="openDeleteMessageDialog(message)"
					/>
				</div>
			</template>
			<AdminDevTicketChangeStatusDialog
				:visible="state.isChangeStatusDialogVisible"
				:data="state.targetTicket"
				@close="state.isChangeStatusDialogVisible = false"
				@done="
					(ticket) => {
						syncTicket(ticket)
						refresh()
					}
				"
			/>
			<AdminDevTicketCreateDialog
				v-model:data="state.targetTicket"
				:visible="state.isCreateDialogVisible"
				@close="state.isCreateDialogVisible = false"
				@done="
					(t) => {
						state.targetTicket = t
						state.targetMessage = null
						state.isSinglePage = true
						refresh()
					}
				"
			/>
			<AdminDevTicketDeleteDialog
				:visible="state.isDeleteDialogVisible"
				:data="state.targetTicket"
				@close="state.isDeleteDialogVisible = false"
				@done="
					() => {
						state.targetTicket = null
						state.targetMessage = null
						state.isSinglePage = false
						refresh()
					}
				"
			/>
			<AdminDevTicketCreateMessageDialog
				v-model:data="state.targetMessage"
				:visible="state.isCreateMessageDialogVisible"
				:ticket="state.targetTicket"
				@close="state.isCreateMessageDialogVisible = false"
				@done="syncTicket"
			/>
			<AdminDevTicketDeleteMessageDialog
				:visible="state.isDeleteMessageDialogVisible"
				:data="state.targetMessage"
				@close="state.isDeleteMessageDialogVisible = false"
				@done="syncTicket"
			/>
		</div>
	</AdminContainer>
</template>

<script setup lang="ts">
import type { DevTicket, DevTicketMessage } from "~/models/dev-ticket"

const devTicketStore = useDevTicketStore()
const route = useRoute()
const app = useNuxtApp()

definePageMeta({
	middleware: ["auth"]
})

const state = reactive({
	loading: true,
	fetched: false,
	searchText: String(route.query.searchText || ""),
	limit: 15,
	isChangeStatusDialogVisible: false,
	isCreateDialogVisible: false,
	isDeleteDialogVisible: false,
	isCreateMessageDialogVisible: false,
	isDeleteMessageDialogVisible: false,
	isSinglePage: false,
	targetTicket: null as DevTicket | null,
	targetMessage: null as DevTicketMessage | null
})

const page = computed(() => Math.max(Number(route.params.page) || 1, 1) || 1)

const statuses = computed(() => devTicketStore.statuses?.filter((v) => !["canceled"].includes(v.value)))

const qSearchText = computed(() => (route.query.searchText || "") as string)

const status = computed(() => statuses.value.find((v) => v.value === route.query.status) || statuses.value[0])

const {
	data,
	status: dataStatus,
	error,
	refresh
} = useLazyAsyncData(
	String(route.path),
	() =>
		devTicketStore.searchDevTickets({
			search: qSearchText.value || "",
			status: status.value.value as any,
			page: page.value,
			limit: state.limit
		}),
	{
		watch: [() => route.query]
	}
)

const search = () => {
	if (state.searchText !== qSearchText.value || page.value !== 1) {
		state.loading = true
		app.$router.replace({
			path: app.$routeUrl.ManageDevTicketsPageUrl(1),
			query: {
				...route.query,
				searchText: state.searchText
			}
		})
	} else {
		refresh()
	}
}

const openChangeStatusDialog = (ticket: DevTicket) => {
	state.targetTicket = JSON.parse(JSON.stringify(ticket))
	useTimeoutFn(() => (state.isChangeStatusDialogVisible = true), 1)
	window.scrollTo({ behavior: "smooth", top: 0 })
}

const openCreateDialog = () => {
	state.targetTicket = {
		priority: "medium"
	} as DevTicket
	useTimeoutFn(() => (state.isCreateDialogVisible = true), 1)
	window.scrollTo({ behavior: "smooth", top: 0 })
}

const openCreateMessageDialog = () => {
	state.targetMessage = {} as DevTicketMessage
	useTimeoutFn(() => (state.isCreateMessageDialogVisible = true), 1)
}

const openDeleteMessageDialog = (message: DevTicketMessage) => {
	state.targetMessage = JSON.parse(JSON.stringify(message))
	useTimeoutFn(() => (state.isDeleteMessageDialogVisible = true), 1)
}

const openDeleteDialog = (ticket: DevTicket) => {
	state.targetTicket = JSON.parse(JSON.stringify(ticket))
	useTimeoutFn(() => (state.isDeleteDialogVisible = true), 1)
	window.scrollTo({ behavior: "smooth", top: 0 })
}

const openTicket = (ticket: DevTicket) => {
	state.targetTicket = JSON.parse(JSON.stringify(ticket))
	state.isSinglePage = true
	window.scrollTo({ behavior: "smooth", top: 0 })
}

const syncTicket = (ticket: DevTicket) => {
	if (state.targetTicket?.id === ticket.id) {
		state.targetTicket = JSON.parse(JSON.stringify(ticket))
	}
	if (data.value?.tickets) {
		data.value.tickets = data.value.tickets.map((v) => (v.id === ticket.id ? ticket : v))
	}
}

const changeStatus = (v: any) => {
	if (status.value.value === v) return
	app.$router.replace({
		path: app.$routeUrl.ManageDevTicketsPageUrl(1),
		query: {
			...route.query,
			status: v
		}
	})
}
</script>
